import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocaleStore, useUIStore } from '@/lib/core/stores';
import { getPage } from '@/services/platform/data/cms';
import type {
  AppSettingsContentResponseModel,
  IApiContentResponseModel,
  PageSettingsPropertiesModel,
} from '@/services/platform/api/model';
import {
  ShopBagScreen,
  ShopDeliveryScreen,
  ShopAddAddressScreen,
  ShopCheckoutScreen,
  ShopProductDetailScreen,
} from '@/components/shared/shop';
import { VenueDetailScreen } from '@/components/shared/explore/detail';
import { EventDetailScreen } from '@/components/shared/events/detail';
import { NotificationsInbox } from '@/components/shared/notifications/inbox';
import { NotFoundError, ServerError } from '@/components/shared/errors';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { setPageMeta, getPageMode, PageMode } from '@/lib/core/utils';
import { AppContext } from '@/lib/core/types';
import { DefaultScreenLayout, screenContentTypeMap } from '@/components/shared/screen/dispatcher';

type PathRemap = (settings: AppSettingsContentResponseModel) => string | undefined;

const PATH_REMAPS: Partial<Record<PageMode, PathRemap>> = {
  [PageMode.BookingDetail]: settings => settings.properties?.bookingDetail?.route?.path,
  [PageMode.ActivityDetail]: settings => settings.properties?.activityDetail?.route?.path,
  [PageMode.ShopCategory]: settings => settings.properties?.shopCategories?.route?.path,
  [PageMode.ShopCollection]: settings => settings.properties?.shopProducts?.route?.path,
  [PageMode.ShopSubcategory]: settings => settings.properties?.shopProducts?.route?.path,
  [PageMode.ShopProduct]: settings => settings.properties?.shopProductDetail?.route?.path,
  [PageMode.ShopBrands]: settings => settings.properties?.brands?.route?.path,
  [PageMode.ShopBrand]: settings => settings.properties?.shopProducts?.route?.path,
};

export function ScreenPage() {
  const navigate = useNavigate();
  const { slug, '*': restPath } = useParams();
  const { pathname } = useLocation();
  const locale = useLocaleStore(state => state.locale);
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);

  const { appSettings } = useApp();
  const translation = useClientTranslation();

  const [content, setContent] = useState<IApiContentResponseModel | undefined>(undefined);
  const [contentFullPath, setContentFullPath] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Routes rendered without a `:slug`/`*` param (e.g. the standalone `/notifications` route,
  // kept outside AppLayout for its own auth gate) fall through to the pathname itself instead
  // of the locale-root fallback, which is only correct for the actual index route ("/").
  const rawPath = restPath
    ? `${slug}/${restPath}`
    : slug || (pathname === '/' ? locale?.split('-')[0] : pathname.slice(1));
  const mode = getPageMode(appSettings, rawPath);
  const fullPath = (appSettings && PATH_REMAPS[mode]?.(appSettings)) ?? rawPath;

  // Client-side checkout flow pages (no CMS content backing)
  const isShopCheckoutFlow =
    mode === PageMode.ShopBag ||
    mode === PageMode.ShopDelivery ||
    mode === PageMode.ShopAddAddress ||
    mode === PageMode.ShopCheckout;
  const isVenueDetail = mode === PageMode.VenueDetail;
  const isEventDetail = mode === PageMode.EventDetail;
  const isNotificationsInbox = mode === PageMode.NotificationsInbox;
  // `content` can briefly belong to the previous path (fetch still in flight,
  // or skipped for venue-detail/event-detail/notifications-inbox/checkout modes) — this stops
  // it from being rendered until it actually matches `fullPath`.
  const isContentCurrent = contentFullPath === fullPath;
  const pageSettings = content?.properties as PageSettingsPropertiesModel;

  useEffect(() => {
    if (isShopCheckoutFlow || isVenueDetail || isEventDetail || isNotificationsInbox) return;

    let isStale = false;

    async function fetchContent() {
      setLoading(true);
      setError(null);

      try {
        const data = await getPage<IApiContentResponseModel>(fullPath, locale, false);
        if (isStale) return;
        setContent(data);
        setContentFullPath(fullPath);
      } catch (err) {
        if (isStale) return;
        setError('Failed to load content');
        console.error(err);
      } finally {
        if (!isStale) setLoading(false);
      }
    }

    fetchContent();

    return () => {
      isStale = true;
    };
  }, [fullPath, locale, isShopCheckoutFlow, isVenueDetail, isEventDetail, isNotificationsInbox]);

  useEffect(() => {
    setScreenHeadOverride(null);
  }, [fullPath, setScreenHeadOverride]);

  const context: AppContext | undefined = content ? { page: content, settings: appSettings } : undefined;

  useEffect(() => {
    setPageMeta({
      title: `${translation('App.Labels.JNcQuoi')} - ${pageSettings?.pageName ?? content?.name}`,
      description: `${translation('App.Labels.JNcQuoi')} - ${content?.name}`,
      keywords: 'react, app',
      author: 'Share IT',
      ogTitle: `${translation('App.Labels.JNcQuoi')} - ${pageSettings?.pageName ?? content?.name}`,
      ogDescription: `${translation('App.Labels.JNcQuoi')} - ${content?.name}`,
      ogImage: undefined,
    });
  }, [content, translation]);

  if (mode === PageMode.ShopBag) return <ShopBagScreen />;
  if (mode === PageMode.ShopDelivery) return <ShopDeliveryScreen />;
  if (mode === PageMode.ShopAddAddress) return <ShopAddAddressScreen />;
  if (mode === PageMode.ShopCheckout) return <ShopCheckoutScreen />;
  if (mode === PageMode.VenueDetail) return <VenueDetailScreen />;
  if (mode === PageMode.EventDetail) return <EventDetailScreen />;
  if (mode === PageMode.NotificationsInbox) return <NotificationsInbox />;

  // ── CMS-driven branches (require content from getPage) ────────
  if (loading || !isContentCurrent) return <SpinLoading wrapperClassName="min-h-[50vh]" />;
  if (error) return <ServerError error={error} />;
  if (!content) return <NotFoundError />;

  if (mode === PageMode.ShopProduct) return <ShopProductDetailScreen content={content} context={context} />;

  const renderer = screenContentTypeMap[content.contentType];
  if (renderer) {
    return renderer({ content, context, fullPath });
  }

  // Fallback for page / appScreen / home / shopCategories / shopProducts / etc.
  return <DefaultScreenLayout content={content} context={context} mode={mode} onBack={() => navigate(-1)} />;
}
